import React from "react";
import { Field, TextareaField, SelectField, YesNo, CheckGroup, SectionTitle, SubHead, Lbl } from "./Fields";
import { DisclaimerSection } from "@/components/DisclaimerSection";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const TITLES = ["Mr", "Mrs", "Miss", "Ms", "Dr", "Other"];
const MARITAL = [{ value: "single", label: "Single" }, { value: "married_cp", label: "Married / CP" }, { value: "lwp", label: "Living with partner" }];
const DSW = [{ value: "none", label: "None" }, { value: "divorced", label: "Divorced" }, { value: "separated", label: "Separated" }, { value: "widowed", label: "Widowed" }];
const READING = ["No", "Big Print", "Read over", "Proxy to sign", "Other"];

const grid = "grid gap-4 sm:grid-cols-2";

function PersonBlock({ v = {}, on, prefix }) {
  return (
    <div className="space-y-4">
      <div className={grid}>
        <SelectField label="Title" value={v.title} onChange={(x) => on("title", x)} options={TITLES} testId={`${prefix}-title`} />
        <Field label="First names" value={v.firstNames} onChange={(x) => on("firstNames", x)} testId={`${prefix}-firstnames`} />
        <Field label="Surname" value={v.surname} onChange={(x) => on("surname", x)} testId={`${prefix}-surname`} />
        <Field label="Date of Birth" type="date" value={v.dob} onChange={(x) => on("dob", x)} testId={`${prefix}-dob`} />
        <SelectField label="Have you been Divorced / Separated / Widowed?" value={v.divSepWid} onChange={(x) => on("divSepWid", x)} options={DSW} testId={`${prefix}-dsw`} />
        <SelectField label="Are you?" value={v.maritalStatus} onChange={(x) => on("maritalStatus", x)} options={MARITAL} testId={`${prefix}-marital`} />
      </div>
      <YesNo label="Assets held under any other name?" value={v.assetsOtherName} onChange={(x) => on("assetsOtherName", x)} testId={`${prefix}-assets-other`} />
      <div className={grid}>
        <YesNo label="Will in contemplation of marriage / civil partnership?" value={v.contemplationMarriage} onChange={(x) => on("contemplationMarriage", x)} testId={`${prefix}-cont-marriage`} />
        <YesNo label="Will in contemplation of divorce?" value={v.contemplationDivorce} onChange={(x) => on("contemplationDivorce", x)} testId={`${prefix}-cont-divorce`} />
      </div>
    </div>
  );
}

// ---------------- Repeatable list helper ----------------
export function ListEditor({ items = [], setItems, blank, render, addLabel, testId }) {
  const add = () => setItems([...(items || []), { ...blank }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const update = (i, key, val) => setItems(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  return (
    <div className="space-y-4">
      {(items || []).map((it, i) => (
        <div key={i} className="rounded-xl border border-brand-line bg-[#FCFBFA] p-4 relative">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-brand-muted">Entry {i + 1}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)} className="text-brand-error h-7 px-2" data-testid={`${testId}-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          {render(it, i, update)}
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} className="border-dashed border-brand-line text-brand-green" data-testid={`${testId}-add`}>
        <Plus className="w-4 h-4 mr-1.5" /> {addLabel}
      </Button>
    </div>
  );
}

// ==================== STEPS ====================
function StepPersonal({ data, patch, setField }) {
  const a = data.testatorA || {};
  const b = data.testatorB || {};
  return (
    <div>
      <SectionTitle hint="Tell us about the person(s) making the will.">Client Details</SectionTitle>
      <SubHead>First Testator (A)</SubHead>
      <PersonBlock v={a} prefix="a" on={(k, val) => patch("testatorA", { [k]: val })} />
      <div className="mt-6">
        <YesNo label="Are you making a will for a spouse / partner as well (Testator B)?" value={data.hasPartner}
          onChange={(x) => setField("hasPartner", x)} testId="has-partner" />
      </div>
      {data.hasPartner && (
        <>
          <SubHead>Spouse / Partner (B)</SubHead>
          <PersonBlock v={b} prefix="b" on={(k, val) => patch("testatorB", { [k]: val })} />
        </>
      )}
    </div>
  );
}

function StepContact({ data, patch }) {
  const c = data.contact || {};
  const on = (k, v) => patch("contact", { [k]: v });
  return (
    <div>
      <SectionTitle hint="How can we reach you?">Contact Details</SectionTitle>
      <div className="space-y-4">
        <Field label="Address" value={c.address} onChange={(x) => on("address", x)} testId="contact-address" />
        <div className={grid}>
          <Field label="City / Town" value={c.city} onChange={(x) => on("city", x)} testId="contact-city" />
          <Field label="County" value={c.county} onChange={(x) => on("county", x)} testId="contact-county" />
          <Field label="Postcode" value={c.postcode} onChange={(x) => on("postcode", x)} testId="contact-postcode" />
          <Field label="Home tel" value={c.homeTel} onChange={(x) => on("homeTel", x)} testId="contact-hometel" />
          <Field label="Daytime tel" value={c.daytimeTel} onChange={(x) => on("daytimeTel", x)} testId="contact-daytel" />
          <Field label="Mobile tel" value={c.mobileTel} onChange={(x) => on("mobileTel", x)} testId="contact-mobiletel" />
          <Field label="Email (A)" type="email" value={c.emailA} onChange={(x) => on("emailA", x)} testId="contact-emaila" />
          {data.hasPartner && <Field label="Email (B)" type="email" value={c.emailB} onChange={(x) => on("emailB", x)} testId="contact-emailb" />}
        </div>
      </div>
    </div>
  );
}

function StepWillHistory({ data, patch }) {
  const w = data.willHistory || {};
  const on = (k, v) => patch("willHistory", { [k]: v });
  return (
    <div>
      <SectionTitle hint="Previous wills and any special requirements.">Will History</SectionTitle>
      <div className="space-y-5">
        <div className={grid}>
          <YesNo label="(A) Have you made a will before?" value={w.madeWillA} onChange={(x) => on("madeWillA", x)} testId="wh-madewill-a" />
          {data.hasPartner && <YesNo label="(B) Have you made a will before?" value={w.madeWillB} onChange={(x) => on("madeWillB", x)} testId="wh-madewill-b" />}
        </div>
        <TextareaField label="(A) What changes are you making?" value={w.changesA} onChange={(x) => on("changesA", x)} testId="wh-changes-a" disabled={w.madeWillA !== true} />
        {data.hasPartner && <TextareaField label="(B) What changes are you making?" value={w.changesB} onChange={(x) => on("changesB", x)} testId="wh-changes-b" disabled={w.madeWillB !== true} />}
        <YesNo label="Is there any medical reason why the will(s) should be treated as express?" value={w.medicalExpress} onChange={(x) => on("medicalExpress", x)} testId="wh-medical" />
        <CheckGroup label="(A) Any problems with reading or signing the will?" options={READING} value={w.readingSigningA} onChange={(x) => on("readingSigningA", x)} testId="wh-reading-a" exclusiveOption="No" />
        {data.hasPartner && <CheckGroup label="(B) Any problems with reading or signing the will?" options={READING} value={w.readingSigningB} onChange={(x) => on("readingSigningB", x)} testId="wh-reading-b" exclusiveOption="No" />}
      </div>
    </div>
  );
}

const ASSET_ROWS = [
  ["homeProperty", "Home & Private Property"], ["propertyAbroad", "Property abroad"], ["buyToLet", "Buy to let property"],
  ["businessAssets", "Business Assets"], ["pension", "Pension"], ["lifeInsurance", "Life Insurance"], ["dis", "D.I.S."],
  ["cashInBank", "Cash in bank"], ["savings", "Savings"], ["shares", "Shares / Investments"], ["inheritance", "Inheritance"],
  ["otherAssets", "Any other assets"], ["mortgage", "Less: Mortgage"], ["otherLiabilities", "Other liabilities"],
];
function StepAssets({ data, patch }) {
  const as = data.assets || {};
  const on = (k, v) => patch("assets", { [k]: v });
  const cell = (row, col) => (as[row]?.[col]) ?? "";
  const setCell = (row, col, val) => on(row, { ...(as[row] || {}), [col]: val });
  return (
    <div>
      <SectionTitle hint="Approximate values help us advise on inheritance tax. Leave blank if unsure.">Assets & Estate</SectionTitle>
      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="text-brand-muted">
              <th className="text-left font-semibold py-2">Asset (£)</th>
              <th className="font-semibold py-2 px-2">Self</th>
              <th className="font-semibold py-2 px-2">Spouse/Partner</th>
              <th className="font-semibold py-2 px-2">Joint</th>
            </tr>
          </thead>
          <tbody>
            {ASSET_ROWS.map(([key, label]) => (
              <tr key={key} className="border-t border-brand-line">
                <td className="py-1.5 pr-2 text-brand-ink">{label}</td>
                {["self", "spouse", "joint"].map((col) => (
                  <td key={col} className="px-1 py-1">
                    <input type="number" value={cell(key, col)} onChange={(e) => setCell(key, col, e.target.value)}
                      className="w-full rounded-md border border-brand-line bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-green"
                      data-testid={`asset-${key}-${col}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SubHead>Foreign Property</SubHead>
      <div className="space-y-4">
        <YesNo label="Any immovable property owned outside of England and Wales?" value={as.foreignOwned} onChange={(x) => on("foreignOwned", x)} testId="asset-foreign-owned" />
        {as.foreignOwned && (
          <>
            <YesNo label="Do you have a will in that country covering that property?" value={as.foreignWill} onChange={(x) => on("foreignWill", x)} testId="asset-foreign-will" />
            <YesNo label='Would you like to make a "Brussels IV" election in your new will? (Your new will revokes the will in that country.)' value={as.brusselsElection} onChange={(x) => on("brusselsElection", x)} testId="asset-brussels" />
          </>
        )}
      </div>
    </div>
  );
}

function StepChildren({ data, patch, setList }) {
  const ch = data.children || {};
  const on = (k, v) => patch("children", { [k]: v });
  return (
    <div>
      <SectionTitle hint="Include all children, including those from previous relationships.">Children & Guardians</SectionTitle>
      <div className={grid}>
        <YesNo label="(A) Have you been married before?" value={ch.marriedBeforeA} onChange={(x) => on("marriedBeforeA", x)} testId="ch-married-a" />
        {data.hasPartner && <YesNo label="(B) Have you been married before?" value={ch.marriedBeforeB} onChange={(x) => on("marriedBeforeB", x)} testId="ch-married-b" />}
      </div>
      <SubHead>Children</SubHead>
      <ListEditor items={data.childrenList} setItems={(x) => setList("childrenList", x)} testId="child"
        blank={{ fullName: "", address: "", postcode: "", ageDob: "", gender: "", whoseChild: "", whoHasPR: "" }}
        addLabel="Add a child"
        render={(it, i, update) => (
          <div className="space-y-3">
            <Field label="Full name" value={it.fullName} onChange={(v) => update(i, "fullName", v)} testId={`child-name-${i}`} />
            <div className={grid}>
              <Field label="Address" value={it.address} onChange={(v) => update(i, "address", v)} testId={`child-address-${i}`} />
              <Field label="Postcode" value={it.postcode} onChange={(v) => update(i, "postcode", v)} testId={`child-postcode-${i}`} />
              <Field label="Age / D.O.B (if minor)" value={it.ageDob} onChange={(v) => update(i, "ageDob", v)} testId={`child-age-${i}`} />
              <SelectField label="M / F" value={it.gender} onChange={(v) => update(i, "gender", v)} options={["M", "F"]} testId={`child-gender-${i}`} />
              <SelectField label="Whose child?" value={it.whoseChild} onChange={(v) => update(i, "whoseChild", v)} options={["A", "B", "Both"]} testId={`child-whose-${i}`} />
              <Field label="Who has parental responsibility?" value={it.whoHasPR} onChange={(v) => update(i, "whoHasPR", v)} testId={`child-pr-${i}`} />
            </div>
          </div>
        )} />
      <SubHead>Guardians (for any minor children)</SubHead>
      <ListEditor items={data.guardians} setItems={(x) => setList("guardians", x)} testId="guardian"
        blank={{ name: "", relationship: "", address: "", postcode: "", tel: "", reserve: false }}
        addLabel="Add a guardian"
        render={(it, i, update) => (
          <div className="space-y-3">
            <div className={grid}>
              <Field label="Name" value={it.name} onChange={(v) => update(i, "name", v)} testId={`guardian-name-${i}`} />
              <Field label="Relationship" value={it.relationship} onChange={(v) => update(i, "relationship", v)} testId={`guardian-rel-${i}`} />
              <Field label="Address" value={it.address} onChange={(v) => update(i, "address", v)} testId={`guardian-address-${i}`} />
              <Field label="Postcode" value={it.postcode} onChange={(v) => update(i, "postcode", v)} testId={`guardian-postcode-${i}`} />
              <Field label="Telephone" value={it.tel} onChange={(v) => update(i, "tel", v)} testId={`guardian-tel-${i}`} />
              <SelectField label="Role" value={it.reserve ? "reserve" : "primary"} onChange={(v) => update(i, "reserve", v === "reserve")} options={[{ value: "primary", label: "Primary" }, { value: "reserve", label: "Joint reserve" }]} testId={`guardian-role-${i}`} />
            </div>
          </div>
        )} />
    </div>
  );
}

const EXEC_OPTIONS = [
  { value: "spouse_solely", label: "Spouse/partner to act solely, with persons below as reserve" },
  { value: "spouse_jointly", label: "Spouse/partner to act jointly with the person(s) below" },
  { value: "persons_only", label: "Only persons below to act" },
];
function StepExecutors({ data, patch, setList }) {
  const ex = data.executors || {};
  const on = (k, v) => patch("executors", { [k]: v });
  return (
    <div>
      <SectionTitle hint="The people who will handle your affairs after death.">Executors</SectionTitle>
      <div className="space-y-4">
        <SelectField label="(A) Executor arrangement" value={ex.optionA} onChange={(x) => on("optionA", x)} options={EXEC_OPTIONS} testId="exec-option-a" />
        {data.hasPartner && <SelectField label="(B) Executor arrangement" value={ex.optionB} onChange={(x) => on("optionB", x)} options={EXEC_OPTIONS} testId="exec-option-b" />}
      </div>
      <SubHead>Appointed executors</SubHead>
      <ListEditor items={data.executorList} setItems={(x) => setList("executorList", x)} testId="exec"
        blank={{ name: "", address: "", relationshipA: "", relationshipB: "", postcode: "", tel: "" }}
        addLabel="Add an executor"
        render={(it, i, update) => (
          <div className="space-y-3">
            <Field label="Name" value={it.name} onChange={(v) => update(i, "name", v)} testId={`exec-name-${i}`} />
            <Field label="Address" value={it.address} onChange={(v) => update(i, "address", v)} testId={`exec-address-${i}`} />
            <div className={grid}>
              <Field label="Relationship to (A)" value={it.relationshipA} onChange={(v) => update(i, "relationshipA", v)} testId={`exec-rela-${i}`} />
              {data.hasPartner && <Field label="Relationship to (B)" value={it.relationshipB} onChange={(v) => update(i, "relationshipB", v)} testId={`exec-relb-${i}`} />}
              <Field label="Postcode" value={it.postcode} onChange={(v) => update(i, "postcode", v)} testId={`exec-postcode-${i}`} />
              <Field label="Tel. No." value={it.tel} onChange={(v) => update(i, "tel", v)} testId={`exec-tel-${i}`} />
            </div>
          </div>
        )} />
    </div>
  );
}

function StepFuneral({ data, patch }) {
  const f = data.funeral || {};
  const on = (k, v) => patch("funeral", { [k]: v });
  const FUN = ["None", "Burial", "Cremation"];
  return (
    <div>
      <SectionTitle hint="Optional funeral wishes.">Funeral Requests</SectionTitle>
      <div className={grid}>
        <SelectField label="Testator A" value={f.aType} onChange={(x) => on("aType", x)} options={FUN} testId="funeral-a" />
        {data.hasPartner && <SelectField label="Testator B" value={f.bType} onChange={(x) => on("bType", x)} options={FUN} testId="funeral-b" />}
      </div>
      <div className="mt-4 space-y-4">
        <YesNo label="Do you have a pre-paid funeral plan?" value={f.prePaid} onChange={(x) => on("prePaid", x)} testId="funeral-prepaid" />
        {f.prePaid && <Field label="Provider name & plan number" value={f.providerPlan} onChange={(x) => on("providerPlan", x)} testId="funeral-provider" />}
        <YesNo label="Would you like more information about funeral plans?" value={f.moreInfo} onChange={(x) => on("moreInfo", x)} testId="funeral-moreinfo" />
      </div>
    </div>
  );
}

function BeneficiaryEditor({ items, setItems, testId, addLabel, hasPartner, withGiftOver = true }) {
  return (
    <ListEditor items={items} setItems={setItems} testId={testId}
      blank={{ name: "", share: "", ageVesting: "", relationshipA: "", relationshipB: "", address: "", giftOverAge: false }}
      addLabel={addLabel}
      render={(it, i, update) => (
        <div className="space-y-3">
          <Field label="Name" value={it.name} onChange={(v) => update(i, "name", v)} testId={`${testId}-name-${i}`} />
          <div className={grid}>
            <Field label="Share (leave blank for equal)" value={it.share} onChange={(v) => update(i, "share", v)} testId={`${testId}-share-${i}`} />
            <Field label="Age of vesting" value={it.ageVesting} onChange={(v) => update(i, "ageVesting", v)} testId={`${testId}-vest-${i}`} />
            <Field label="Relationship to (A)" value={it.relationshipA} onChange={(v) => update(i, "relationshipA", v)} testId={`${testId}-rela-${i}`} />
            {hasPartner && <Field label="Relationship to (B)" value={it.relationshipB} onChange={(v) => update(i, "relationshipB", v)} testId={`${testId}-relb-${i}`} />}
          </div>
          <Field label="Address" value={it.address} onChange={(v) => update(i, "address", v)} testId={`${testId}-address-${i}`} />
        </div>
      )} />
  );
}

function StepResidual({ data, patch, setList }) {
  const r = data.residual || {};
  const on = (k, v) => patch("residual", { [k]: v });
  return (
    <div>
      <SectionTitle hint="Who inherits the remainder of your estate.">Residual Estate & Beneficiaries</SectionTitle>
      <div className="space-y-4">
        <YesNo label="Residue to pass firstly to spouse / partner?" value={r.toSpouse} onChange={(x) => on("toSpouse", x)} testId="res-spouse" />
        <YesNo label="…and then to sons / daughters in equal shares?" value={r.thenChildren} onChange={(x) => on("thenChildren", x)} testId="res-children" />
        <YesNo label="Include future unborn children?" value={r.includeUnborn} onChange={(x) => on("includeUnborn", x)} testId="res-unborn" />
        <YesNo label="Should the estate pass to specific named beneficiaries instead?" value={r.toNamed} onChange={(x) => on("toNamed", x)} testId="res-named" />
      </div>
      {r.toNamed && (<><SubHead>Named beneficiaries</SubHead>
        <BeneficiaryEditor items={data.beneficiaries} setItems={(x) => setList("beneficiaries", x)} testId="ben" addLabel="Add a beneficiary" hasPartner={data.hasPartner} /></>)}
      <SubHead>Reserve beneficiaries (if the above fails entirely)</SubHead>
      <BeneficiaryEditor items={data.reserveBeneficiaries} setItems={(x) => setList("reserveBeneficiaries", x)} testId="rben" addLabel="Add a reserve beneficiary" hasPartner={data.hasPartner} />
    </div>
  );
}

function StepLegacies({ data, patch, setList }) {
  const ex = data.exclusions || {};
  const onEx = (k, v) => patch("exclusions", { [k]: v });
  return (
    <div>
      <SectionTitle hint="Specific gifts and any exclusions.">Legacies & Exclusions</SectionTitle>
      <SubHead>Individual legacies / bequests</SubHead>
      <ListEditor items={data.legacies} setItems={(x) => setList("legacies", x)} testId="leg"
        blank={{ recipient: "", charityNo: "", relationship: "", address: "", secondDeath: "", description: "", vestingAge: "", giftOverAge: false }}
        addLabel="Add a legacy / bequest"
        render={(it, i, update) => (
          <div className="space-y-3">
            <div className={grid}>
              <Field label="Recipient" value={it.recipient} onChange={(v) => update(i, "recipient", v)} testId={`leg-recipient-${i}`} />
              <Field label="Charity No. (if applicable)" value={it.charityNo} onChange={(v) => update(i, "charityNo", v)} testId={`leg-charity-${i}`} />
              <Field label="Relationship" value={it.relationship} onChange={(v) => update(i, "relationship", v)} testId={`leg-rel-${i}`} />
              <SelectField label="Applies on" value={it.secondDeath} onChange={(v) => update(i, "secondDeath", v)} options={[{ value: "second_death", label: "Second death only" }, { value: "only_a", label: "Only (A)" }, { value: "only_b", label: "Only (B)" }]} testId={`leg-when-${i}`} />
            </div>
            <Field label="Address" value={it.address} onChange={(v) => update(i, "address", v)} testId={`leg-address-${i}`} />
            <TextareaField label="Description or amount" value={it.description} onChange={(v) => update(i, "description", v)} testId={`leg-desc-${i}`} rows={2} />
          </div>
        )} />
      <SubHead>Exclusions</SubHead>
      <div className="space-y-4">
        <YesNo label="Have you deliberately excluded anyone who may have a possible claim on your estate?" value={ex.excluded} onChange={(x) => onEx("excluded", x)} testId="excl-any" />
        {ex.excluded && (<>
          <YesNo label="Is the exclusion mutually agreed?" value={ex.mutuallyAgreed} onChange={(x) => onEx("mutuallyAgreed", x)} testId="excl-agreed" />
          <TextareaField label="Name the people who may claim they have been excluded (name, relationship, address)" value={ex.people} onChange={(x) => onEx("people", x)} testId="excl-people" />
          <YesNo label="I/we have been advised to leave a letter of explanation regarding the above exclusion(s)." value={ex.letterAdvised} onChange={(x) => onEx("letterAdvised", x)} testId="excl-letter" />
        </>)}
      </div>
    </div>
  );
}

const VAT_RATE = 0.2;
const withVat = (exVat) => Math.round(exVat * (1 + VAT_RATE));
const PACKAGE_INFO = {
  single_will: { label: "Single Will", exVat: 200, incVat: withVat(200), desc: "One will for a single testator." },
  mirror_will: { label: "Mirror Will", exVat: 300, incVat: withVat(300), desc: "A matching pair of wills for a couple." },
};
function StepService({ data, patch }) {
  const s = data.service || {};
  const choose = (id) => patch("service", { package: id });
  return (
    <div>
      <SectionTitle hint="Choose the service that suits you. Payment is taken on the next step.">Select Your Service</SectionTitle>
      <div className="grid gap-4 sm:grid-cols-2">
        {Object.entries(PACKAGE_INFO).map(([id, p]) => {
          const active = s.package === id;
          return (
            <button key={id} type="button" onClick={() => choose(id)} data-testid={`service-${id}`}
              className={`text-left rounded-xl border p-5 transition-all duration-300 ${active ? "border-brand-green ring-2 ring-brand-green/30 bg-brand-admin" : "border-brand-line bg-white hover:-translate-y-0.5 hover:shadow-md"}`}>
              <div className="flex items-center justify-between">
                <span className="font-serif text-xl text-brand-green">{p.label}</span>
                {active && <span className="text-xs font-semibold text-white bg-brand-green px-2 py-0.5 rounded-full">Selected</span>}
              </div>
              <p className="text-sm text-brand-muted mt-1">{p.desc}</p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-brand-ink">£{p.incVat}</span>
                <span className="text-sm text-brand-muted">inc. VAT (£{p.exVat} + VAT)</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepDeclarations({ data, patch }) {
  return (
    <div>
      <SectionTitle hint="Please review and select any additional services before choosing your package.">Declarations & Elected Services</SectionTitle>
      <DisclaimerSection variant="will" value={data.disclaimer || {}} onChange={(v) => patch("disclaimer", v)} />
    </div>
  );
}

export const STEPS = [
  { key: "personal", label: "Client Details", Component: StepPersonal },
  { key: "contact", label: "Contact Details", Component: StepContact },
  { key: "history", label: "Will History", Component: StepWillHistory },
  { key: "assets", label: "Assets & Estate", Component: StepAssets },
  { key: "children", label: "Children & Guardians", Component: StepChildren },
  { key: "executors", label: "Executors", Component: StepExecutors },
  { key: "funeral", label: "Funeral Requests", Component: StepFuneral },
  { key: "residual", label: "Residual & Beneficiaries", Component: StepResidual },
  { key: "legacies", label: "Legacies & Exclusions", Component: StepLegacies },
  { key: "declarations", label: "Declarations", Component: StepDeclarations },
  { key: "service", label: "Select Service", Component: StepService },
];

export { PACKAGE_INFO };
